import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { saveJob, removeSavedJob, getSavedJobIds } from "../../services/jobService";
import { generateEmbedding, findSimilarJobs } from "../../services/semanticSearchService";
import JobList from "./JobList";
import LoadingSpinner from "../UI/LoadingSpinner";
import ToggleSwitch from "../UI/ToggleSwitch";
import { parseDate } from "../../utils/parsers";

/**
 * All Jobs page component (main dashboard)
 * @returns {JSX.Element} All Jobs page
 */
const AllJobsPage = () => {
  const { user } = useAuth();
  
  // Data and loading states
  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savedJobIds, setSavedJobIds] = useState([]);
  // Loading state for specific job save/unsave operations
  const [savingJobId, setSavingJobId] = useState(null);
  const [saveJobError, setSaveJobError] = useState(null);
  
  // Semantic search states
  const [useSemanticSearch, setUseSemanticSearch] = useState(false);
  const [semanticSearchResults, setSemanticSearchResults] = useState([]);
  const [similarityScores, setSimilarityScores] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [jobEmbeddings, setJobEmbeddings] = useState(null);
  const searchDebounceTimeout = useRef(null);
  
  // Essential Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [orgCountryDropdownVisible, setOrgCountryDropdownVisible] = useState(false);
  
  // Additional filters
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [languageExclusive, setLanguageExclusive] = useState(false);
  
  // Binary filters (via drop-down: "all", "yes", "no")
  const [filterPhd, setFilterPhd] = useState("all");
  const [filterMasters, setFilterMasters] = useState("all");
  const [filterRemote, setFilterRemote] = useState("all");
  const [filterInternship, setFilterInternship] = useState("all");
  
  // Recently added filter
  const [filterRecentlyAdded, setFilterRecentlyAdded] = useState("all");
  
  // Sort state
  const [sortOption, setSortOption] = useState("datecloses_asc");
  
  // Toggle state for showing/hiding additional filters
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  
  // Pagination (can be implemented later if needed)
  const RESULTS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  
  // Fetch all jobs
  useEffect(() => {
    fetch("/clean.JSON")
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to load JSON data");
        }
        return res.json();
      })
      .then((data) => {
        setJobsData(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading jobs data:", error);
        setError("Failed to load jobs data");
        setLoading(false);
      });
  }, []);
  
  // Load job embeddings for semantic search
  useEffect(() => {
    const loadEmbeddings = async () => {
      try {
        // Fetch the pre-computed embeddings
        const response = await fetch('/embeddings.json');
        if (!response.ok) {
          console.warn('Embeddings file not found. Semantic search will be disabled.');
          // If semantic search is enabled, disable it since embeddings are missing
          if (useSemanticSearch) {
            setUseSemanticSearch(false);
            setError("Semantic search unavailable - embeddings not found");
          }
          return;
        }
        
        const data = await response.json();
        
        if (!data || !Array.isArray(data) || data.length === 0) {
          console.warn('Embeddings file is empty or invalid. Semantic search will be disabled.');
          // If semantic search is enabled, disable it since embeddings are missing
          if (useSemanticSearch) {
            setUseSemanticSearch(false);
            setError("Semantic search unavailable - embeddings file empty");
          }
          return;
        }
        
        setJobEmbeddings(data);
        console.log(`Loaded ${data.length} job embeddings for semantic search`);
        
        // Log a sample of what the embeddings look like
        if (data.length > 0) {
          console.log("Sample embedding entry:", {
            job_id: data[0].job_id,
            cis_fuss_id: data[0].cis_fuss_id,
            embedding_dimensions: data[0].embedding ? data[0].embedding.length : 'missing'
          });
        }
      } catch (error) {
        console.error('Error loading embeddings:', error);
        // If semantic search is enabled, disable it since embeddings failed to load
        if (useSemanticSearch) {
          setUseSemanticSearch(false);
          setError("Semantic search unavailable - error loading embeddings");
        }
      }
    };
    
    loadEmbeddings();
  }, [useSemanticSearch]);
  
  // Fetch user's saved job IDs - refetched whenever user changes OR a job is saved/removed
  const [refreshSavedJobs, setRefreshSavedJobs] = useState(0);
  
  useEffect(() => {
    const fetchSavedJobs = async () => {
      if (!user) {
        setSavedJobIds([]);
        return;
      }
      
      try {
        // Clear any previous errors
        setSaveJobError(null);
        
        console.info("AllJobsPage: Fetching saved jobs for user:", user.id);
        const jobIds = await getSavedJobIds(user.id);
        
        // Log the result for debugging
        console.info(`AllJobsPage: Retrieved ${jobIds.length} saved jobs for user ${user.id}`);
        
        setSavedJobIds(jobIds);
      } catch (error) {
        console.error("AllJobsPage: Error fetching saved jobs:", error);
        setSaveJobError(`Failed to load saved jobs`);
      }
    };
    
    fetchSavedJobs();
  }, [user, refreshSavedJobs]);
  
  // Available filter options derived from data
  const availableOrganizations = useMemo(() => {
    const orgs = [...new Set(jobsData.map((job) => job.organization))].filter(Boolean);
    return orgs.sort();
  }, [jobsData]);
  
  const availableCountries = useMemo(() => {
    const countries = [...new Set(jobsData.map((job) => job.country))].filter(Boolean);
    return countries.sort();
  }, [jobsData]);
  
  const availableLevels = useMemo(() => {
    const levels = [...new Set(jobsData.map((job) => job.level))].filter(Boolean);
    return levels.sort();
  }, [jobsData]);
  
  const availableCities = useMemo(() => {
    // Filter cities based on selected countries
    let cities = jobsData
      .filter(job => selectedCountries.length === 0 || selectedCountries.includes(job.country))
      .map(job => job.city)
      .filter(Boolean);
    
    // Get unique cities and sort
    cities = [...new Set(cities)].sort();
    return cities;
  }, [jobsData, selectedCountries]);
  
  // eslint-disable-next-line
  const availableGrades = useMemo(() => {
    const grades = [...new Set(jobsData.map(job => job.grade))].filter(Boolean);
    return grades.sort();
  }, [jobsData]);
  
  const availableLanguages = useMemo(() => {
    // Collect all languages from all jobs
    const allLanguages = new Set();
    jobsData.forEach(job => {
      if (job.languagesrequired) {
        try {
          let languages;
          if (typeof job.languagesrequired === 'string') {
            if (job.languagesrequired.startsWith('[') && job.languagesrequired.endsWith(']')) {
              languages = JSON.parse(job.languagesrequired);
            } else {
              languages = job.languagesrequired.split(',').map(lang => lang.trim());
            }
          } else if (Array.isArray(job.languagesrequired)) {
            languages = job.languagesrequired;
          }
          
          if (languages) {
            languages.forEach(lang => {
              if (lang) allLanguages.add(lang.trim());
            });
          }
        } catch (e) {
          // If parsing fails, add the raw string
          if (job.languagesrequired) {
            allLanguages.add(job.languagesrequired);
          }
        }
      }
    });
    
    return [...allLanguages].sort();
  }, [jobsData]);
  
  // Helper to toggle selections in checkbox groups
  const toggleSelection = (value, currentSelection, setSelection) => {
    if (currentSelection.includes(value)) {
      setSelection(currentSelection.filter(item => item !== value));
    } else {
      setSelection([...currentSelection, value]);
    }
  };
  
  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedOrganizations([]);
    setSelectedCountries([]);
    setSelectedCities([]);
    setSelectedGrades([]);
    setSelectedLevels([]);
    setSelectedLanguages([]);
    setLanguageExclusive(false);
    setFilterPhd("all");
    setFilterMasters("all");
    setFilterRemote("all");
    setFilterInternship("all");
    setFilterRecentlyAdded("all");
    setSortOption("datecloses_asc");
  };
  
  // Perform semantic search
  const performSemanticSearch = async (query) => {
    if (!query.trim()) {
      console.log("Empty query, skipping semantic search");
      setSemanticSearchResults([]);
      setSimilarityScores({});
      return;
    }
    
    if (!jobEmbeddings || !Array.isArray(jobEmbeddings) || jobEmbeddings.length === 0) {
      console.error("Job embeddings not loaded yet or empty");
      setError("Semantic search unavailable: embeddings not loaded");
      setSemanticSearchResults([]);
      setSimilarityScores({});
      return;
    }
    
    setIsSearching(true);
    setError(""); // Clear any previous errors
    
    try {
      console.log(`Starting semantic search for: "${query}"`);
      
      // Generate embedding for the query
      const { embedding, error: embeddingError } = await generateEmbedding(query);
      
      if (embeddingError) {
        console.error("Embedding generation error:", embeddingError);
        throw new Error(`Embedding error: ${embeddingError}`);
      }
      
      if (!embedding) {
        console.error("No embedding returned");
        throw new Error("Failed to generate embedding for search query");
      }
      
      console.log(`Generated embedding with ${embedding.length} dimensions`);
      
      // Find similar jobs using the embedding
      const { results, error: searchError } = findSimilarJobs(embedding, jobEmbeddings);
      
      if (searchError) {
        console.error("Error finding similar jobs:", searchError);
        throw new Error(`Search error: ${searchError}`);
      }
      
      if (!results || !Array.isArray(results)) {
        console.error("Invalid results format:", results);
        throw new Error("Invalid search results format");
      }
      
      console.log(`Found ${results.length} semantically similar jobs`);
      
      // Since semantic search now directly returns cis_fuss_id values,
      // we no longer need the mapping logic that was previously required
      
      // Process results to simplify the structure and verify we have valid IDs
      const processedResults = results.map(result => {
        return {
          cis_fuss_id: result.cis_fuss_id,
          similarity: result.similarity,
          // Mark all results as mapped since they now use cis_fuss_id directly
          mapped: result.cis_fuss_id !== undefined
        };
      });
      
      // Log any missing IDs
      const unmappedCount = processedResults.filter(r => !r.mapped).length;
      if (unmappedCount > 0) {
        console.warn(`${unmappedCount} semantic results have missing cis_fuss_id values`);
      }
      
      // Extract cis_fuss_id values from results
      const jobIds = processedResults
        .filter(result => result.mapped)
        .map(result => result.cis_fuss_id);
      
      // Create a map of cis_fuss_id -> similarity score for sorting
      const scores = {};
      processedResults.forEach(result => {
        // Store similarity scores by cis_fuss_id (which is what the filter uses)
        if (result.cis_fuss_id !== undefined) {
          scores[result.cis_fuss_id] = result.similarity;
        }
      });
      
      // Log some of the top results with their similarity scores
      if (processedResults.length > 0) {
        console.log("Top matches:");
        processedResults.slice(0, 5).forEach(result => {
          console.log(`- CIS_FUSS_ID: ${result.cis_fuss_id}, Similarity: ${result.similarity.toFixed(4)}`);
        });
        
        // Add debug logging to verify scores object structure
        console.log("First few similarity score entries:", 
          Object.entries(scores).slice(0, 3).map(([id, score]) => `cis_fuss_id ${id}: ${score.toFixed(4)}`));
      }
      
      // Store both the job IDs and the similarity scores
      setSemanticSearchResults(jobIds);
      setSimilarityScores(scores);
      
      // Automatically set sort option to search relevance
      setSortOption("search_relevance");
    } catch (error) {
      console.error('Semantic search error:', error);
      setError(`Semantic search failed: ${error.message}`);
      setSemanticSearchResults([]);
      setSimilarityScores({});
      
      // Fallback to keyword search on error
      console.log("Falling back to keyword search");
      if (useSemanticSearch) {
        setUseSemanticSearch(false);
      }
    } finally {
      setIsSearching(false);
    }
  };
  
  // Check if a job should be included based on all filters
  const passesAllFilters = (job) => {
    // Search term filter
    if (searchTerm) {
      if (useSemanticSearch && semanticSearchResults.length > 0) {
        // For semantic search, ONLY check if cis_fuss_id is in semanticSearchResults
        // This ensures we use the semantic similarity results from the AI embedding
        if (!semanticSearchResults.includes(job.cis_fuss_id)) {
          return false;
        }
        // We no longer return true here - continue to apply additional filters
        // This allows semantic search results to be further filtered by other criteria
      } else {
        // Enhanced keyword search logic (only used when semantic search is OFF)
        const searchTermLower = searchTerm.toLowerCase();
        // Create a regex that matches whole words or partial words at start/end of text
        const searchRegex = new RegExp(`\\b${searchTermLower}|${searchTermLower}\\b`, 'i');
        
        // Check multiple fields
        const titleMatch = job.title && (
          job.title.toLowerCase().includes(searchTermLower) || 
          searchRegex.test(job.title)
        );
        
        const orgMatch = job.organization && (
          job.organization.toLowerCase().includes(searchTermLower) ||
          searchRegex.test(job.organization)
        );
        
        // Check both job_description (full) and description fields
        const fullDescMatch = job.job_description && (
          job.job_description.toLowerCase().includes(searchTermLower) ||
          searchRegex.test(job.job_description)
        );
        
        // Also check short description as fallback
        const shortDescMatch = job.descript_short && (
          job.descript_short.toLowerCase().includes(searchTermLower) ||
          searchRegex.test(job.descript_short)
        );
        
        if (!(titleMatch || orgMatch || fullDescMatch || shortDescMatch)) {
          return false;
        }
      }
    }
    
    // Organization filter
    if (selectedOrganizations.length > 0 && !selectedOrganizations.includes(job.organization)) {
      return false;
    }
    
    // Country filter
    if (selectedCountries.length > 0 && !selectedCountries.includes(job.country)) {
      return false;
    }
    
    // City filter
    if (selectedCities.length > 0 && !selectedCities.includes(job.city)) {
      return false;
    }
    
    // Level filter
    if (selectedLevels.length > 0 && !selectedLevels.includes(job.level)) {
      return false;
    }
    
    // Grade filter
    if (selectedGrades.length > 0 && !selectedGrades.includes(job.grade)) {
      return false;
    }
    
    // Internship filter
    if (filterInternship !== "all") {
      const isInternship = job.internship === "Yes" || job.internship === "yes" || job.internship === true || job.internship === 1 || job.internship === "1";
      if (filterInternship === "yes" && !isInternship) return false;
      if (filterInternship === "no" && isInternship) return false;
    }
    
    // Remote filter
    if (filterRemote !== "all") {
      const isRemote = job.remote === "Yes" || job.remote === "yes" || job.remote === true || job.remote === 1 || job.remote === "1";
      if (filterRemote === "yes" && !isRemote) return false;
      if (filterRemote === "no" && isRemote) return false;
    }
    
    // PhD filter
    if (filterPhd !== "all") {
      const requiresPhd = job.phdrequired === "Yes" || job.phdrequired === "yes" || job.phdrequired === true || job.phdrequired === 1 || job.phdrequired === "1";
      if (filterPhd === "yes" && !requiresPhd) return false;
      if (filterPhd === "no" && requiresPhd) return false;
    }
    
    // Masters filter
    if (filterMasters !== "all") {
      const requiresMasters = job.mastersrequired === "Yes" || job.mastersrequired === "yes" || job.mastersrequired === true || job.mastersrequired === 1 || job.mastersrequired === "1";
      if (filterMasters === "yes" && !requiresMasters) return false;
      if (filterMasters === "no" && requiresMasters) return false;
    }
    
    // Languages filter
    if (selectedLanguages.length > 0 && job.languagesrequired) {
      let jobLanguages = [];
      
      try {
        if (typeof job.languagesrequired === 'string') {
          if (job.languagesrequired.startsWith('[') && job.languagesrequired.endsWith(']')) {
            jobLanguages = JSON.parse(job.languagesrequired);
          } else {
            jobLanguages = job.languagesrequired.split(',').map(lang => lang.trim());
          }
        } else if (Array.isArray(job.languagesrequired)) {
          jobLanguages = job.languagesrequired;
        }
      } catch (e) {
        jobLanguages = [job.languagesrequired];
      }
      
      if (languageExclusive) {
        // Exclusive mode: Every language required by the job must be in my selected languages
        // This filters out jobs requiring languages I don't speak
        return jobLanguages.every(jobLang => 
          selectedLanguages.some(lang => jobLang.trim().toLowerCase() === lang.toLowerCase())
        );
      } else {
        // Inclusive mode: job must contain AT LEAST ONE selected language
        return selectedLanguages.some(lang => 
          jobLanguages.some(jobLang => jobLang.trim().toLowerCase() === lang.toLowerCase())
        );
      }
    }
    
    // Recently Added filter
    if (filterRecentlyAdded !== "all" && job.first_seen) {
      try {
        const firstSeen = new Date(job.first_seen);
        const now = new Date();
        const dayDiff = (now - firstSeen) / (1000 * 60 * 60 * 24);
        
        if (filterRecentlyAdded === "1d" && dayDiff > 1) return false;
        if (filterRecentlyAdded === "3d" && dayDiff > 3) return false;
        if (filterRecentlyAdded === "7d" && dayDiff > 7) return false;
      } catch (e) {
        // If date parsing fails, include the job
      }
    }
    
    return true;
  };
  
  // Filter jobs based on all criteria
  const filteredJobs = useMemo(() => {
    if (!jobsData.length) return [];
    
    // Log for debugging
    if (useSemanticSearch && semanticSearchResults.length > 0) {
      console.log(`Filtering with semantic search: ${semanticSearchResults.length} semantic results`);
    }
    
    return jobsData.filter(passesAllFilters);
  }, [
    jobsData, 
    passesAllFilters,
    semanticSearchResults,  // Add dependency on semantic results
    useSemanticSearch,      // Add dependency on semantic search toggle
    searchTerm              // Add dependency on search term
  ]);
  
  // Sort filtered jobs
  const sortedJobs = useMemo(() => {
    if (!filteredJobs.length) return [];
    
    return [...filteredJobs].sort((a, b) => {
      // Handle search relevance sorting (for semantic search)
      if (sortOption === "search_relevance") {
        const scoreA = similarityScores[a.cis_fuss_id] || 0;
        const scoreB = similarityScores[b.cis_fuss_id] || 0;
        
        // Sort by similarity score (highest first)
        return scoreB - scoreA;
      }
      
      // Handle date posted sorting
      if (sortOption === "dateposted_desc" || sortOption === "dateposted_asc") {
        const dateA = parseDate(a.datejobposted);
        const dateB = parseDate(b.datejobposted);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return sortOption === "dateposted_desc" ? dateB - dateA : dateA - dateB;
      }
      
      // Handle date closes sorting
      if (sortOption === "datecloses_desc" || sortOption === "datecloses_asc") {
        const dateA = parseDate(a.datecloses);
        const dateB = parseDate(b.datecloses);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return sortOption === "datecloses_desc" ? dateB - dateA : dateA - dateB;
      }
      
      // Handle title sorting
      if (sortOption === "title_asc" || sortOption === "title_desc") {
        return sortOption === "title_asc"
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title);
      }
      
      // Handle CV similarity sorting
      if (sortOption === "cv_similarity") {
        const simA = a.cv_similarity !== undefined ? a.cv_similarity : 0;
        const simB = b.cv_similarity !== undefined ? b.cv_similarity : 0;
        
        return simB - simA; // Highest first
      }
      
      return 0;
    });
  }, [filteredJobs, sortOption, similarityScores]);
  
  // Save job handler - can accept either cis_fuss_id (number) or a job object
  const handleSaveJob = useCallback(async (jobOrId) => {
    if (!user) return;
    
    console.log("handleSaveJob received:", jobOrId);
    if (typeof jobOrId === 'object') {
      console.log("Job object properties:", Object.keys(jobOrId));
      console.log("cis_fuss_id value:", jobOrId.cis_fuss_id);
    }
    
    // Determine if we were passed a job object or just the ID
    const isCisFussId = typeof jobOrId === 'number' || typeof jobOrId === 'string';
    const jobId = isCisFussId ? jobOrId : (jobOrId && jobOrId.cis_fuss_id);
    
    // Validate job ID
    if (!jobId && jobId !== 0) {  // Allow ID 0 as valid
      console.error("AllJobsPage: No valid cis_fuss_id available:", jobOrId);
      setError("Cannot save job: missing cis_fuss_id");
      return;
    }
    
    // Set loading state - ensure numeric for comparison
    setSavingJobId(Number(jobId));
    setSaveJobError(null);
    
    try {
      console.info(`AllJobsPage: Saving job ${jobId} for user ${user.id}`);
      
      // Call the service with just the cis_fuss_id
      const success = await saveJob(user.id, jobId);
      
      if (!success) {
        setSaveJobError(`Failed to save job`);
        return;
      }
      
      console.info(`AllJobsPage: Job ${jobId} saved successfully`);
      
      // Force refresh saved jobs from the database to ensure UI consistency
      setRefreshSavedJobs(prev => prev + 1);
      
      // Show success message briefly
      setError("Job saved successfully!");
      setTimeout(() => setError(""), 2000);
      
    } catch (error) {
      console.error("AllJobsPage: Error saving job:", error);
      setError(`Error saving job`);
    } finally {
      // Clear loading state
      setSavingJobId(null);
    }
  }, [user]);
  
  // Remove job handler
  const handleRemoveJob = useCallback(async (jobId) => {
    if (!user) return;
    
    // Set loading state - ensure numeric for comparison
    setSavingJobId(Number(jobId));
    setSaveJobError(null);
    
    try {
      console.info(`AllJobsPage: Removing job ${jobId} for user ${user.id}`);
      
      // Call the service
      const success = await removeSavedJob(user.id, jobId);
      
      if (!success) {
        setSaveJobError(`Failed to remove job`);
        return;
      }
      
      console.info(`AllJobsPage: Job ${jobId} removed successfully`);
      
      // Force refresh saved jobs from the database to ensure UI consistency
      setRefreshSavedJobs(prev => prev + 1);
      
      // Show success message briefly
      setError("Job removed successfully!");
      setTimeout(() => setError(""), 2000);
      
    } catch (error) {
      console.error("AllJobsPage: Error removing job:", error);
      setError(`Error removing job`);
    } finally {
      // Clear loading state
      setSavingJobId(null);
    }
  }, [user]);
  
  // Styles
  const containerStyle = {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "20px",
  };
  
  const filterSectionStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "10px",
    border: "1px solid #333",
    borderRadius: "8px",
    backgroundColor: "#1a1a1a",
    marginBottom: "20px",
    color: "#ffffff",
  };
  
  const rowStyle = {
    display: "flex",
    gap: "20px",
    flexWrap: "wrap",
    alignItems: "flex-start",
  };
  
  const filterGroupStyle = {
    flex: "1 1 180px",
    border: "1px solid #333",
    borderRadius: "4px",
    padding: "8px",
    backgroundColor: "#2a2a2a",
    color: "#ffffff",
  };
  
  const binaryFilterStyle = {
    flex: "1 1 120px",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  };
  
  const checkboxContainerStyle = {
    maxHeight: "150px",
    overflowY: "auto",
    paddingRight: "5px",
    marginTop: "5px",
    color: "#ffffff",
  };
  
  const toggleButtonStyle = {
    width: "100%",
    margin: "10px 0",
    padding: "12px",
    cursor: "pointer",
    borderRadius: "4px",
    border: "1px solid #007bff",
    backgroundColor: "#007bff",
    color: "#fff",
    textAlign: "center",
  };
  
  // Pagination
  const totalPages = Math.ceil(sortedJobs.length / RESULTS_PER_PAGE);
  const startIndex = (currentPage - 1) * RESULTS_PER_PAGE;
  const paginatedJobs = sortedJobs.slice(startIndex, startIndex + RESULTS_PER_PAGE);
  
  return (
    <div style={containerStyle}>
      <h1 style={{ textAlign: "center" }}>
        <span style={{ color: "#2980B9" }}>cis</span>
        <span style={{ color: "#CE93D8" }}>fuss</span>
      </h1>
      
      {error && <div style={{ color: "red", padding: "15px", marginBottom: "20px" }}>{error}</div>}
      
      {/* Essential Filters */}
      <div style={filterSectionStyle}>
        {/* Row 1: Search with semantic toggle */}
        <div style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", justifyContent: "flex-start" }}>
            <ToggleSwitch
              isOn={useSemanticSearch}
              handleToggle={() => {
                const newValue = !useSemanticSearch;
                setUseSemanticSearch(newValue);
                
                // Clear semantic results when switching back to keyword search
                if (!newValue) {
                  setSemanticSearchResults([]);
                  setSimilarityScores({});
                  // Reset to default sort option when turning off semantic search
                  setSortOption("datecloses_asc");
                } else if (searchTerm.trim().length > 2) {
                  // Trigger semantic search immediately when switching to it with existing query
                  performSemanticSearch(searchTerm);
                  // Note: performSemanticSearch will set the sort option to search_relevance
                } else if (newValue) {
                  // If turning on semantic search but no query yet, still set the sort option
                  setSortOption("search_relevance");
                }
              }}
              labelLeft="Keyword"
              labelRight="Semantic"
            />
          </div>
          
          {/* Search input on its own line */}
          <div style={{ width: "100%", marginBottom: "10px" }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                
                if (useSemanticSearch && e.target.value.trim().length > 2) {
                  // Debounce to avoid too many API calls
                  clearTimeout(searchDebounceTimeout.current);
                  searchDebounceTimeout.current = setTimeout(() => {
                    performSemanticSearch(e.target.value);
                  }, 500);
                } else if (!e.target.value.trim()) {
                  setSemanticSearchResults([]);
                }
              }}
              placeholder={useSemanticSearch 
                ? "Search with AI-powered semantic matching... ⚡" 
                : "Search title or description"}
              style={{ 
                width: "100%", 
                padding: "8px", 
                boxSizing: "border-box",
                borderColor: useSemanticSearch ? "#39FF14" : "#333",
                backgroundColor: "#2a2a2a",
                color: "#ffffff",
                borderRadius: "5px",
                boxShadow: useSemanticSearch ? "0 0 8px #39FF14" : "none", // Glow effect when semantic search is on
                outline: "none", // Remove default focus outline
              }}
            />
            
            {useSemanticSearch && searchTerm && semanticSearchResults.length > 0 && (
              <div style={{ fontSize: "13px", color: "#39FF14", marginTop: "5px" }}>
                ⚡ Showing the {semanticSearchResults.length} most semantically similar jobs
              </div>
            )}
          </div>
        </div>
        
        {/* Row 2: Organization & Country */}
        <div style={rowStyle}>
          <div style={filterGroupStyle}>
            {/* Clickable header to toggle both drop-downs */}
            <div
              onClick={() => setOrgCountryDropdownVisible(!orgCountryDropdownVisible)}
              style={{
                padding: "8px",
                border: "1px solid #333",
                cursor: "pointer",
                backgroundColor: "#2a2a2a",
                color: "#ffffff",
                borderRadius: "5px",
              }}
            >
              {selectedOrganizations.length > 0
                ? selectedOrganizations.join(", ")
                : "Select Organization"}
            </div>
            {orgCountryDropdownVisible && (
              <div style={checkboxContainerStyle}>
                {availableOrganizations.map((org) => (
                  <label key={org} style={{ display: "block", color: "#ffffff" }}>
                    <input
                      type="checkbox"
                      checked={selectedOrganizations.includes(org)}
                      onChange={() => toggleSelection(org, selectedOrganizations, setSelectedOrganizations)}
                    />{" "}
                    {org}
                  </label>
                ))}
              </div>
            )}
          </div>
          
          <div style={filterGroupStyle}>
            {/* Clickable header to toggle both drop-downs */}
            <div
              onClick={() => setOrgCountryDropdownVisible(!orgCountryDropdownVisible)}
              style={{
                padding: "8px",
                border: "1px solid #333",
                cursor: "pointer",
                backgroundColor: "#2a2a2a",
                color: "#ffffff",
                borderRadius: "5px",
              }}
            >
              {selectedCountries.length > 0
                ? selectedCountries.join(", ")
                : "Select Country"}
            </div>
            {orgCountryDropdownVisible && (
              <div style={checkboxContainerStyle}>
                {availableCountries.map((country) => (
                  <label key={country} style={{ display: "block", color: "#ffffff" }}>
                    <input
                      type="checkbox"
                      checked={selectedCountries.includes(country)}
                      onChange={() => toggleSelection(country, selectedCountries, setSelectedCountries)}
                    />{" "}
                    {country}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Row 3: Sort Options and Reset Button */}
        <div style={rowStyle}>
          <div style={binaryFilterStyle}>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              style={{ 
                padding: "8px",
                backgroundColor: "#2a2a2a",
                color: "#ffffff",
                border: "1px solid #333",
                borderRadius: "5px"
              }}
            >
              {useSemanticSearch && (
                <option value="search_relevance">🔍 Search Relevance</option>
              )}
              <option value="datecloses_asc">📅 Date Closes (Soonest)</option>
              <option value="datecloses_desc">📅 Date Closes (Latest)</option>
              <option value="dateposted_desc">🗓️ Date Posted (Newest first)</option>
              <option value="dateposted_asc">🗓️ Date Posted (Oldest first)</option>
              <option value="title_asc">🔠 Title (A-Z)</option>
              <option value="title_desc">🔠 Title (Z-A)</option>
              <option value="cv_similarity">🤖 CV Similarity (Highest first)</option>
            </select>
          </div>
          
          <div style={{ ...binaryFilterStyle, alignSelf: "flex-end" }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: "10px",
                borderRadius: "5px",
                border: "none",
                backgroundColor: "#007bff",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>
      
      {/* Toggle Additional Filters */}
      <button
        onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
        style={toggleButtonStyle}
      >
        {showAdditionalFilters ? "Hide Additional Filters" : "Additional Filters"}
      </button>
      
      {/* Additional Filters Section */}
      {showAdditionalFilters && (
        <div style={{ ...filterSectionStyle, transition: "max-height 0.5s ease" }}>
          <div style={rowStyle}>
            {/* Language Filter */}
            <div style={filterGroupStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                🗣️ Languages I Speak
              </span>
              <div style={checkboxContainerStyle}>
                {availableLanguages.map((lang) => (
                  <label key={lang} style={{ display: "block", color: "#ffffff" }}>
                    <input
                      type="checkbox"
                      checked={selectedLanguages.includes(lang)}
                      onChange={() => toggleSelection(lang, selectedLanguages, setSelectedLanguages)}
                    />{" "}
                    {lang}
                  </label>
                ))}
              </div>
              <label style={{ display: "block", marginTop: "5px", color: "#ffffff" }}>
                <input
                  type="checkbox"
                  checked={languageExclusive}
                  onChange={() => setLanguageExclusive(!languageExclusive)}
                />{" "}
                Only show jobs requiring languages I speak
              </label>
            </div>
            
            {/* Grade Filter */}
            <div style={filterGroupStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                📈 Grade
              </span>
              <div style={checkboxContainerStyle}>
                {availableGrades.map((grade) => (
                  <label key={grade} style={{ display: "block" }}>
                    <input
                      type="checkbox"
                      checked={selectedGrades.includes(grade)}
                      onChange={() => toggleSelection(grade, selectedGrades, setSelectedGrades)}
                    />{" "}
                    {grade}
                  </label>
                ))}
              </div>
            </div>
            
            {/* City Filter */}
            <div style={filterGroupStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                🏙️ City
              </span>
              <div style={checkboxContainerStyle}>
                {availableCities.map((city) => (
                  <label key={city} style={{ display: "block" }}>
                    <input
                      type="checkbox"
                      checked={selectedCities.includes(city)}
                      onChange={() => toggleSelection(city, selectedCities, setSelectedCities)}
                    />{" "}
                    {city}
                  </label>
                ))}
              </div>
            </div>
            
            {/* Level Filter */}
            <div style={filterGroupStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                📊 Level
              </span>
              <div style={checkboxContainerStyle}>
                {availableLevels.map((level) => (
                  <label key={level} style={{ display: "block" }}>
                    <input
                      type="checkbox"
                      checked={selectedLevels.includes(level)}
                      onChange={() => toggleSelection(level, selectedLevels, setSelectedLevels)}
                    />{" "}
                    {level}
                  </label>
                ))}
              </div>
            </div>
          </div>
          
          <div style={rowStyle}>
            {/* Binary Filters Row */}
            <div style={binaryFilterStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                🎓 PhD Required
              </span>
              <select
                value={filterPhd}
                onChange={(e) => setFilterPhd(e.target.value)}
                style={{ padding: "8px" }}
              >
                <option value="all">All Jobs</option>
                <option value="yes">PhD Required</option>
                <option value="no">No PhD Required</option>
              </select>
            </div>
            
            <div style={binaryFilterStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                🎓 Masters Required
              </span>
              <select
                value={filterMasters}
                onChange={(e) => setFilterMasters(e.target.value)}
                style={{ padding: "8px" }}
              >
                <option value="all">All Jobs</option>
                <option value="yes">Masters Required</option>
                <option value="no">No Masters Required</option>
              </select>
            </div>
            
            <div style={binaryFilterStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                🌐 Remote
              </span>
              <select
                value={filterRemote}
                onChange={(e) => setFilterRemote(e.target.value)}
                style={{ padding: "8px" }}
              >
                <option value="all">All Jobs</option>
                <option value="yes">Remote Only</option>
                <option value="no">On-site Only</option>
              </select>
            </div>
            
            <div style={binaryFilterStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                🎯 Internship
              </span>
              <select
                value={filterInternship}
                onChange={(e) => setFilterInternship(e.target.value)}
                style={{ padding: "8px" }}
              >
                <option value="all">All Jobs</option>
                <option value="yes">Internships Only</option>
                <option value="no">No Internships</option>
              </select>
            </div>
            
            <div style={binaryFilterStyle}>
              <span style={{ fontWeight: "bold", marginBottom: "5px", display: "block", color: "#ffffff" }}>
                🆕 Added to cisfuss
              </span>
              <select
                value={filterRecentlyAdded}
                onChange={(e) => setFilterRecentlyAdded(e.target.value)}
                style={{ padding: "8px" }}
              >
                <option value="all">All Jobs</option>
                <option value="1d">Last 24 Hours</option>
                <option value="3d">Last 3 Days</option>
                <option value="7d">Last 7 Days</option>
              </select>
            </div>
          </div>
        </div>
      )}
      
      {/* Job Count */}
      <div style={{ margin: "20px 0", fontWeight: "bold" }}>
        {loading ? "Loading..." : `${sortedJobs.length} jobs`}
      </div>
      
      {/* Job Save Error Message */}
      {saveJobError && (
        <div style={{ 
          backgroundColor: "#f8d7da", 
          color: "#721c24", 
          padding: "10px", 
          borderRadius: "5px", 
          marginBottom: "20px" 
        }}>
          {saveJobError}
        </div>
      )}
      
      {/* Job Listings */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <JobList
          jobs={paginatedJobs}
          savedJobIds={savedJobIds}
          onSaveJob={handleSaveJob}
          onRemoveJob={handleRemoveJob}
          showSaveButtons={!!user}
          savingJobId={savingJobId}
        />
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", margin: "20px 0" }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={currentPage === 1 ? { 
              padding: "8px 12px",
              margin: "0 5px",
              borderRadius: "4px",
              border: "1px solid #007bff",
              backgroundColor: "#007bff",
              color: "#fff",
              opacity: 0.5,
              cursor: "default",
            } : {
              padding: "8px 12px",
              margin: "0 5px",
              borderRadius: "4px",
              border: "1px solid #007bff",
              backgroundColor: "#007bff",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Previous
          </button>
          
          <span style={{ margin: "0 10px", lineHeight: "38px" }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={currentPage === totalPages ? {
              padding: "8px 12px",
              margin: "0 5px",
              borderRadius: "4px",
              border: "1px solid #007bff",
              backgroundColor: "#007bff",
              color: "#fff",
              opacity: 0.5,
              cursor: "default",
            } : {
              padding: "8px 12px",
              margin: "0 5px",
              borderRadius: "4px",
              border: "1px solid #007bff",
              backgroundColor: "#007bff",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AllJobsPage;